/**
 * Persistent bottom mini-player. Visible on every page once a track is
 * loaded; auto-hides when there's no current track so empty sessions
 * don't have visual chrome.
 *
 * Render is fixed to the viewport bottom — sits above the mobile
 * BottomNav (which is z-index ~10). Inset adjusts for the sidebar on
 * desktop so the bar doesn't overlap the nav.
 */
import { Icon } from "../brand";
import { usePlayer } from "./PlayerContext";
import { useResponsive } from "../../hooks/useResponsive";

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function MiniPlayer() {
  const {
    track,
    isPlaying,
    loadingId,
    currentTime,
    duration,
    lastError,
    togglePlay,
    stop,
    seek,
  } = usePlayer();
  const { isMobile } = useResponsive();

  // Stand-alone error toast when there's no track loaded yet (e.g. the
  // very first click fails). Once a track loads, the error appears
  // inline above the transport instead.
  if (!track && !lastError) return null;
  if (!track && lastError) {
    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          left: isMobile ? 0 : 240,
          right: 0,
          bottom: isMobile ? 64 : 0,
          zIndex: 50,
          background: "rgba(255,107,94,0.12)",
          borderTop: "1px solid rgba(255,107,94,0.4)",
          padding: "10px 16px",
          fontSize: 13,
          color: "#FF6B5E",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Icon name="x" size={12} color="#FF6B5E" />
        Couldn&apos;t play: {lastError.message}
      </div>
    );
  }
  // Narrow track to non-null for the rest of the render — TS can't
  // tell from the early return above because `track` is referenced
  // via state, not a discriminated union.
  if (!track) return null;

  const onClickProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    seek(pct * duration);
  };

  const loading = loadingId === track.id;

  return (
    <div
      role="region"
      aria-label="Now playing"
      style={{
        position: "fixed",
        // 240 = sidebar width on desktop; mobile gets full width.
        left: isMobile ? 0 : 240,
        right: 0,
        // Sit above the mobile bottom-nav (64px). Desktop has no
        // bottom nav so we sit flush to the viewport bottom.
        bottom: isMobile ? 64 : 0,
        zIndex: 50,
        background: "var(--bg-elev)",
        borderTop: "1px solid var(--border)",
        backdropFilter: "blur(12px)",
        display: "grid",
        gridTemplateColumns: isMobile ? "auto 1fr auto" : "minmax(200px, 1fr) auto minmax(200px, 1fr)",
        alignItems: "center",
        gap: 12,
        padding: "8px 16px",
      }}
    >
      {/* Left: track text */}
      <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 4,
            background: "linear-gradient(135deg,#10131d,#17203b,#26324d)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="music" size={16} color="#A1A1A6" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {track.title}
          </div>
          {lastError && lastError.id === track.id ? (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "#FF6B5E",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {lastError.message}
            </div>
          ) : (
            track.subtitle && (
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--fg-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {track.subtitle}
              </div>
            )
          )}
        </div>
      </div>

      {/* Center: transport */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={() => void togglePlay(track.id, track)}
          disabled={loading}
          style={{
            width: 36,
            height: 36,
            borderRadius: 9999,
            border: "1px solid var(--border)",
            background: "var(--fg)",
            color: "var(--bg)",
            cursor: loading ? "wait" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <Icon
            name={isPlaying ? "player-pause-filled" : "player-play-filled"}
            size={14}
            color="var(--bg)"
          />
        </button>
        <button
          aria-label="Close player"
          onClick={stop}
          style={{
            width: 28,
            height: 28,
            borderRadius: 9999,
            border: "none",
            background: "transparent",
            color: "var(--fg-muted)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="x" size={12} />
        </button>
      </div>

      {/* Right: progress (desktop only — mobile is too narrow) */}
      {!isMobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            justifySelf: "end",
            width: "100%",
            maxWidth: 380,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--fg-muted)",
              minWidth: 36,
              textAlign: "right",
            }}
          >
            {fmt(currentTime)}
          </span>
          <div
            onClick={onClickProgress}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={currentTime}
            tabIndex={0}
            style={{
              flex: 1,
              height: 4,
              background: "var(--border)",
              borderRadius: 9999,
              cursor: duration ? "pointer" : "default",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: duration ? `${Math.min(100, (currentTime / duration) * 100)}%` : "0%",
                height: "100%",
                background: "#FF6B22",
                transition: "width 100ms linear",
              }}
            />
          </div>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--fg-muted)",
              minWidth: 36,
            }}
          >
            {fmt(duration)}
          </span>
        </div>
      )}
    </div>
  );
}
